import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { C } from './ambApi';

export default function AmbassadorTerms() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen py-12 px-4" style={{ background: C.cream }} data-testid="ambassador-terms">
      <div className="max-w-3xl mx-auto">
        <button onClick={() => navigate(-1)} className="mb-6 inline-flex items-center gap-2 text-sm font-semibold" style={{ color: C.navy }} data-testid="terms-back">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="bg-white rounded-3xl shadow-lg p-8 sm:p-10">
          <p className="text-sm font-bold tracking-wide" style={{ color: C.gold }}>EASYPASSING</p>
          <h1 className="text-3xl font-bold mt-1 mb-2" style={{ color: C.navy }}>Ambassador Terms &amp; Commission Policy</h1>
          <p className="text-sm mb-8" style={{ color: C.mute }}>Last updated: June 2026</p>

          <div className="space-y-7 text-[15px] leading-relaxed" style={{ color: '#3F4A55' }}>
            <Section n="1" title="The Ambassador Program">
              The EasyPassing Legacy Ambassador Program lets approved individuals earn compensation for referring new
              customers to EasyPassing. By applying, being approved, and using your referral link or code, you agree to
              these terms and to our general <a href="/terms" target="_blank" rel="noreferrer" style={{ color: C.navy2, fontWeight: 600 }}>Terms of Service</a> and{' '}
              <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: C.navy2, fontWeight: 600 }}>Privacy Policy</a>.
            </Section>

            <Section n="2" title="Eligibility &amp; Approval">
              You must be at least 18 years old and provide accurate information in your application. All applications are
              reviewed and approved at EasyPassing's sole discretion. We may decline, suspend, or terminate any ambassador
              at any time, for any reason, including violation of these terms.
            </Section>

            <Section n="3" title="How You Earn — Commission Structure">
              <ul className="list-disc pl-5 space-y-2 mt-1">
                <li><b>$10 upfront bonus</b> — a one-time bonus each time a person you referred becomes a <i>paid</i> subscriber for the first time.</li>
                <li><b>10% recurring commission</b> — 10% of each qualifying monthly payment made by a referred paid subscriber, for up to <b>12 months</b> from their first paid date.</li>
                <li>Commissions are calculated on the actual amount paid (net of discounts, taxes, and fees) and are attributed only to the ambassador whose referral code was locked to that customer at signup.</li>
              </ul>
            </Section>

            <Section n="4" title="Referral Attribution">
              A referral is credited when a new customer signs up using your referral link or enters your referral code at
              registration. Attribution is <b>locked to the first ambassador</b> associated with that customer and cannot be
              changed afterward. Referral tracking relies on cookies and the code stored at signup; clearing cookies or
              signing up through another ambassador's link may affect attribution.
            </Section>

            <Section n="5" title="Commission Approval &amp; Holding Period">
              <ul className="list-disc pl-5 space-y-2 mt-1">
                <li>All commissions are first recorded as <b>pending</b>.</li>
                <li>Commissions become <b>approved</b> after a <b>30-day holding period</b>, provided the underlying payment has not been refunded, charged back, or cancelled.</li>
                <li>Only <b>approved</b> commissions are eligible for payout.</li>
              </ul>
            </Section>

            <Section n="6" title="Reversals, Refunds &amp; Cancellations">
              If a referred customer's payment is refunded, disputed/charged back, or their subscription is cancelled before
              a commission is paid, the related commissions will be <b>reversed</b> and removed from your balance. Recurring
              commissions stop when the referred customer cancels or their 12-month window ends.
            </Section>

            <Section n="7" title="Payouts">
              <ul className="list-disc pl-5 space-y-2 mt-1">
                <li>Payouts are issued for <b>approved</b> commissions via your selected method (PayPal, Venmo, CashApp, or check).</li>
                <li>You are responsible for providing accurate payout details. EasyPassing is not liable for payments sent to an incorrect address you provided.</li>
                <li>You are responsible for any taxes owed on the compensation you earn. You may be required to provide tax information (e.g., a W-9) before payout.</li>
              </ul>
            </Section>

            <Section n="8" title="Prohibited Conduct">
              To keep the program fair, you may <b>not</b>:
              <ul className="list-disc pl-5 space-y-2 mt-1">
                <li>Refer yourself, create fake accounts, or generate fraudulent clicks or signups.</li>
                <li>Use spam, misleading claims, or unauthorized paid advertising on EasyPassing's brand or trademarks.</li>
                <li>Make guarantees or statements about EasyPassing that are untrue, or present the service as a substitute for legal, financial, or estate-planning advice.</li>
                <li>Bid on EasyPassing branded keywords or impersonate EasyPassing.</li>
              </ul>
              Suspected fraud or abuse may result in withheld commissions, account suspension, and forfeiture of unpaid balances.
            </Section>

            <Section n="9" title="Use of Your Name &amp; Image">
              You grant EasyPassing permission to display your submitted name and profile photo on your personal referral
              landing page. Profile photos are subject to review and may be removed if inappropriate.
            </Section>

            <Section n="10" title="Changes to the Program">
              EasyPassing may modify commission rates, holding periods, payout methods, or these terms at any time.
              Material changes will be communicated to active ambassadors. Continued participation after changes take effect
              constitutes acceptance of the updated terms.
            </Section>

            <Section n="11" title="Termination">
              Either party may end participation at any time. Upon termination, approved-but-unpaid commissions earned in
              good standing will be paid according to the normal schedule; pending commissions and any balances associated
              with terminated-for-cause accounts may be forfeited.
            </Section>

            <Section n="12" title="Contact">
              Questions about the program or your commissions? Email{' '}
              <a href="mailto:ambassadors@easypassing.life" style={{ color: C.navy2, fontWeight: 600 }}>ambassadors@easypassing.life</a>.
            </Section>
          </div>

          <div className="mt-10 pt-6 border-t" style={{ borderColor: C.line }}>
            <button onClick={() => navigate('/ambassador/apply')} className="px-6 h-11 rounded-full text-white font-semibold" style={{ background: C.navy }} data-testid="terms-apply">
              Back to application
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Section = ({ n, title, children }) => (
  <section>
    <h2 className="text-lg font-bold mb-1.5" style={{ color: C.ink }}>{n}. <span dangerouslySetInnerHTML={{ __html: title }} /></h2>
    <div>{children}</div>
  </section>
);
